import React, { useState, useEffect, useMemo } from "react";
import images from "../../assets/images";
import "../../index.css";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ValidationModule,
  themeQuartz,
  TextEditorModule,
} from "ag-grid-community";
import { Link } from "react-router-dom";
import axiosInstance from "../../common/axiosInstance";
import { premisesApiResponse, RowData } from "../../types/api";
import { ChevronDown, MapPin } from "lucide-react";
import { usePremisesStore, useRegionStore } from "../../store/store";
import { Dropdown } from "../../components/dropdown/Dropdown";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextEditorModule,
  ValidationModule,
]);

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const Premises: React.FC = () => {
  const [rowData, setRowData] = useState<RowData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");

  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();

  useEffect(() => {
    if (!regions.length || !allPremises.length) return;

    const transformedData = allPremises.map((item: any) => {
      const regionsData = regions.find((r: any) => r._id === item.region);

      return {
        id: item.id,
        premisesName: item.premisesName,
        addressLine1: item.addressLine1,
        city: item.townCity,
        region: regionsData?.regionName || "N/A",
        lastUpdate: new Date(item.updatedAt).toLocaleDateString("en-US"),
        status: item.isStatus,
        action: null,
      };
    });
    setRowData(transformedData);
  }, [allPremises, regions]);

  useEffect(() => {
    fetchPremises();
    if (!regions.length) fetchRegions();
  }, []);

  const toggleStatus = async (id: string | number, status: boolean) => {
    try {
      // setIsLoading(true);
      const response = await axiosInstance.put(`/api/premises/toggle-status/${id}`, { isStatus: !status });
    } catch (error) {
      console.error("Error toggling status:", error);
    } finally {
      fetchPremises();
    }
  };
  // get All Premises

  // search premises
  const searchPremises = async (keyword: any) => {
    try {
      setIsLoading(true);

      if (keyword) {
        const response = await axiosInstance.get(`/api/premises/search/${keyword}`);
        const transformedData: RowData[] = response.data.map(
          (item: premisesApiResponse) => {
            const regionsData = regions?.find(
              (r: any) => r._id === item.region,
            );

            return {
              id: item.id,
              premisesName: item.premisesName,
              addressLine1: item.addressLine1,
              city: item.townCity,
              region: regionsData?.regionName || "N/A",
              lastUpdate: new Date(item.updatedAt).toLocaleDateString("en-US"),
              status: item.isStatus,
            };
          },
        );
        setRowData(transformedData);
      } else {
        fetchPremises();
        if (!regions.length) fetchRegions();
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  // filter region
  const filterByRegion = async (id: any) => {
    try {
      setIsLoading(true);
      if (id === "All Region" || id === "") {
        // Fix: Call fetchPremises and handle loading state properly
        await fetchPremises();
        setIsLoading(false);
      } else {

        const response = await axiosInstance.get(`/api/premises/filter/region/${id}`);

        const transformedData: RowData[] = response.data.map(
          (item: premisesApiResponse) => {
            const regionsData = regions?.find(
              (r: any) => r._id === item.region,
            );
            return {
              id: item.id,
              premisesName: item.premisesName,
              addressLine1: item.addressLine1,
              city: item.townCity,
              region: regionsData?.regionName,
              lastUpdate: new Date(item.updatedAt).toLocaleDateString("en-US"),
              status: item.isStatus,
              action: null,
            };
          },
        );
        setRowData(transformedData);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  //filter by region
  const filterByPremises = async (id: any) => {
    try {
      setIsLoading(true);
      if (id === "All Premises" || id === "") {
        await fetchPremises();
        setIsLoading(false);
      } else {

        const response = await axiosInstance.get(`/api/premises/filter/premises/${id}`);

        const transformedData: RowData[] = response.data.map(
          (item: premisesApiResponse) => {
            const regionsData = regions?.find(
              (r: any) => r._id === item.region,
            );

            return {
              id: item.id,
              premisesName: item.premisesName,
              addressLine1: item.addressLine1,
              city: item.townCity,
              region: regionsData?.regionName,
              lastUpdate: new Date(item.updatedAt).toLocaleDateString("en-US"),
              status: item.isStatus,
              action: null,
            };
          },
        );
        setRowData(transformedData);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const [columnDefs, setColDefs] = useState<any>([
    {
      field: "premisesName",
      headerName: "Premises",
      sortable: false,
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: { padding: "10px 0 0 5px " },
    },
    {
      field: "addressLine1",
      cellEditor: "agTextCellEditor",
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1,
      cellEditorParams: {
        maxLength: 20,
      },
      headerName: "Address Line 1",
      sortable: false,
      cellStyle: { padding: "10px 0 0 0 " },
      filter: false,
    },
    {
      field: "city",
      headerName: "Town /City",
      sortable: false,
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1,
      filter: false,
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      field: "region",
      headerName: "Region",
      headerStyle: { backgroundColor: "#e9f5ff" },
      sortable: false,
      flex: 1,
      filter: false,
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      field: "lastUpdate",
      headerName: "Last Update",
      sortable: false,
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1,
      filter: false,
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 20,
      flex: 1,
      filter: false,
      cellStyle: { padding: "10px 0 0 0" },
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellRenderer: (params: any) => {
        const isActive = params.data.status === true;

        return (
          <button
            type="button"
            onClick={() => toggleStatus(params.data.id, params.data.status)}
            className={`relative flex items-center w-24 h-8 rounded-full font-medium text-sm
        transition-colors duration-300
        focus:outline-none focus:ring-0 focus:border-none
        ${isActive ? "bg-teal-100 text-teal-800" : "bg-red-100 text-gray-800"}`}
          >
            <span className={`absolute z-10 ${isActive ? "left-2" : "left-9"}`}>
              {isActive ? "Active" : "Inactive"}
            </span>

            <span
              className={`absolute w-6 h-6 rounded-full transition-transform duration-300 ${
                isActive
                  ? "bg-teal-500 translate-x-[64px]"
                  : "bg-gray-400 translate-x-1"
              }`}
            />
          </button>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 0 0 0 " },
      minWidth: 20,
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3 pt-2">
          <Link to={`/view-premises/${params.data.id}`}>
            <img className="iconTd" src={images.viewIcon} />
          </Link>
          <Link to={`/update-premises/${params.data.id}`}>
            <img className="iconTd" src={images.editIcon} />
          </Link>
        </div>
      ),
      filter: false,
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
    };
  }, []);
  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <div className=" w-full  px-3">
      <div className="page-header mt-20">
        <h2 className="header-title ml-2">Premises List</h2>

        <div className="dashHeaderRight">
          <Link
            to="/add-premises"
            className="flex btn-primary roundedBt iconWithBt text-uppercase"
          >
            <img src={images.addPremiessIcon} alt="" />
            <span>ADD PREMISES</span>
          </Link>
        </div>
      </div>

      <div className="tableBlock w-full ">
        <div className="card">
          <div className="tableSearchBlock d-flex justify-content-between">
            <div className="leftTableSearchBlock">
              <div className="input-affix">
                <i className="prefix-icon anticon anticon-search"></i>
                <input
                  type="text"
                  className="form-control"
                  onChange={(e) => searchPremises(e.target.value)}
                  placeholder="Search Here..."
                />
              </div>
            </div>

            <span className="flex gap-8">
              {/* Regions Dropdown */}
              <Dropdown
                selectedValue={selectedRegion}
                onChange={(value) => {
                  setSelectedRegion(value);
                  setSelectedPremises("");
                  filterByRegion(value);
                }}
                type="region"
              />
              {/* Premises Dropdown */}
              <Dropdown
                selectedValue={selectedPremises}
                onChange={(value) => {
                  setSelectedPremises(value);
                  filterByPremises(value);
                }}
                type="premises"
                selectedRegion={selectedRegion}
              />
            </span>
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="ag-theme-alpine w-full h-[650px] pl-2 border-0 rounded-lg shadow-lg">
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                theme={theme}
                headerHeight={65}
                rowHeight={50}
                defaultColDef={defaultColDef}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Premises;
